/**
 * ContractDetailModal — Detalle completo de un contrato con documento adjunto/enlace.
 */
import { useState } from 'react'
import { Pencil, FileText, ExternalLink, Download, Eye, Calendar, DollarSign, Clock, Briefcase, Folder } from 'lucide-react'
import type { EmploymentContract } from '../hooks/usePayroll'
import { useEmployeeDocuments, formatFileSize } from '../hooks/useHrDocuments'
import { useTenantLocale, formatDate } from '@/lib/locale'
import { useToast } from '@/components/Toast'
import { extractErrorMessage } from '@/lib/errorUtils'
import apiClient from '@/lib/apiClient'
import { documentoGuardado, useVisorPdf } from '@/components/VisorPdf'
import { Modal } from '@/components/ds/Modal'

interface Props {
  contract: EmploymentContract | null
  onClose: () => void
  onEdit: (contract: EmploymentContract) => void
}

const TYPE_LABEL: Record<string, string> = {
  indefinido: 'Contrato Indefinido',
  plazo_fijo: 'Contrato a Plazo Fijo',
  obra: 'Contrato por Obra o Faena',
}

export default function ContractDetailModal({ contract, onClose, onEdit }: Props) {
  const locale = useTenantLocale()

  if (!contract) return null

  const c = contract as any // extended fields
  const hasDocument = c.document_file_url || c.document_url

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="Detalle del Contrato"
      subtitle={c.employee_name}
      actions={
        <>
          <button onClick={() => onEdit(contract)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary border border-primary-100 rounded-lg hover:bg-primary-50">
            <Pencil size={12} /> Editar
          </button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Info principal */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <InfoField icon={Briefcase} label="Tipo de Contrato"
            value={<span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
              c.contract_type === 'indefinido' ? 'bg-success-surface-strong text-success-content' :
              c.contract_type === 'plazo_fijo' ? 'bg-warning-surface-strong text-warning-content' :
              'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
            }`}>{TYPE_LABEL[c.contract_type] || c.contract_type_display}</span>}
          />
          <InfoField icon={DollarSign} label="Sueldo Base"
            value={<span className="text-primary font-bold">${parseFloat(c.base_salary).toLocaleString('es-CL')}</span>}
          />
          <InfoField icon={DollarSign} label="Gratificación"
            value={c.gratification_mode === 'fixed'
              ? `Fija: $${parseFloat(c.gratification_fixed_amount || '0').toLocaleString('es-CL')}`
              : (c.gratification_mode_display || 'Legal (25% con tope)')}
          />
          <InfoField icon={Clock} label="Jornada" value={`${c.work_hours_weekly} hrs/semana`} />
          <InfoField label="Cargo" value={c.position || '—'} />
          <InfoField label="Departamento" value={c.department || '—'} />
          <InfoField label="Estado" value={
            c.is_current
              ? <span className="text-success-content-soft font-semibold">Vigente</span>
              : <span className="text-content-subtle">No vigente</span>
          } />
          <InfoField icon={Calendar} label="Fecha Inicio" value={formatDate(c.start_date, locale)} />
          <InfoField icon={Calendar} label="Fecha Término" value={c.end_date ? formatDate(c.end_date, locale) : 'Indefinido'} />
          {c.document_signed_at && (
            <InfoField icon={Calendar} label="Fecha Firma" value={formatDate(c.document_signed_at, locale)} />
          )}
        </div>

        {/* Notas */}
        {c.notes && (
          <div>
            <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider mb-2">Observaciones</h3>
            <p className="text-sm text-content-muted bg-surface-muted rounded-lg p-3">{c.notes}</p>
          </div>
        )}

        {/* Documento del contrato */}
        <div>
          <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <FileText size={12} /> Documento del Contrato
          </h3>

          {hasDocument ? (
            <div className="space-y-3">
              {/* Archivo adjunto */}
              {c.document_file_url && (
                <div className="flex items-center gap-3 p-4 bg-glass-3 border border-divider rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-danger-surface flex items-center justify-center flex-shrink-0">
                    <FileText size={20} className="text-danger-solid" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-content truncate">Contrato firmado</p>
                    <p className="text-xs text-content-subtle">Archivo adjunto</p>
                  </div>
                  <a href={c.document_file_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary border border-primary-100 rounded-lg hover:bg-primary-50">
                    <Download size={12} /> Descargar
                  </a>
                </div>
              )}

              {/* Enlace externo */}
              {c.document_url && (
                <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-100 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                    <ExternalLink size={20} className="text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-content">Documento externo</p>
                    <p className="text-xs text-blue-500 truncate">{c.document_url}</p>
                  </div>
                  <a href={c.document_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-600 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40">
                    <ExternalLink size={12} /> Abrir
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-divider rounded-xl text-content-subtle">
              <FileText size={32} className="mb-2 text-content-subtle" />
              <p className="text-sm">Sin documento adjunto</p>
              <p className="text-xs mt-1">Edite el contrato para adjuntar un archivo o enlace externo.</p>
            </div>
          )}
        </div>

        {/* Documentos relacionados (carpeta digital) */}
        <ContractRelatedDocuments contract={c} />
      </div>
    </Modal>
  )
}

function ContractRelatedDocuments({ contract }: { contract: EmploymentContract }) {
  const { data, isLoading } = useEmployeeDocuments({
    employeeId: contract.employee,
    contract: contract.id,
  })
  const docs = data?.documents ?? []
  const toast = useToast()
  const { ver, visor } = useVisorPdf()

  const handleDownload = async (docId: string) => {
    try {
      const res = await apiClient.get<{ url: string }>(`/hr-documents/documents/${docId}/download/`)
      const url = res.data?.url
      if (!url) {
        toast.error('No se pudo obtener el enlace de descarga.')
        return
      }
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo descargar el documento.'))
    }
  }

  return (
    <div>
      <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider mb-3 flex items-center gap-1.5">
        <Folder size={12} /> Documentos Relacionados
      </h3>
      {isLoading ? (
        <p className="text-sm text-content-subtle py-4">Cargando documentos...</p>
      ) : docs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-6 border-2 border-dashed border-divider rounded-xl text-content-subtle">
          <FileText size={24} className="mb-2 text-content-subtle" />
          <p className="text-sm">No hay documentos vinculados a este contrato</p>
          <p className="text-xs mt-1">Vincula anexos, modificaciones o liquidaciones desde la carpeta digital del empleado.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map(d => (
            <div
              key={d.id}
              className="flex items-center gap-3 p-3 bg-glass-3 border border-divider rounded-xl"
            >
              <FileText size={16} className="text-content-subtle shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-content truncate">{d.name}</p>
                <div className="flex items-center gap-2 text-xs text-content-subtle">
                  {d.document_type_name && <span>{d.document_type_name}</span>}
                  <span>·</span>
                  <span>{formatFileSize(d.file_size)}</span>
                  <span>·</span>
                  <span>{new Date(d.uploaded_at).toLocaleDateString('es-CL')}</span>
                </div>
              </div>
              {(d.mime_type || '').toLowerCase() === 'application/pdf' ? (
                <button
                  onClick={() => ver({
                    titulo: d.name,
                    nombreArchivo: d.name.toLowerCase().endsWith('.pdf') ? d.name : `${d.name}.pdf`,
                    ...documentoGuardado(apiClient, `/hr-documents/documents/${d.id}/download/`),
                  })}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary border border-primary-100 rounded-lg hover:bg-primary-50"
                >
                  <Eye size={12} aria-hidden="true" /> Ver
                </button>
              ) : (
                <button
                  onClick={() => handleDownload(d.id)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary border border-primary-100 rounded-lg hover:bg-primary-50"
                >
                  <Download size={12} /> Descargar
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {visor}
    </div>
  )
}

function InfoField({ icon: Icon, label, value }: { icon?: any; label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-content-subtle font-medium mb-0.5 flex items-center gap-1">
        {Icon && <Icon size={10} />} {label}
      </p>
      <div className="text-sm text-content">{value}</div>
    </div>
  )
}
